/**
 * Shell-owned states every screen reuses (docs/ux/shell.md "Required
 * states"): the empty and error panels, the offline banner, the session
 * expired dialog, the route announcer and the skip link. Copy is plain and
 * exact; errors say what did not happen and offer Retry, never the raw error.
 */

import { cn } from 'cn';
import { useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { useAnnouncement } from '@/app/announcer';
import { UNAUTHENTICATED_EVENT } from '@/app/api';
import { useOnline } from '@/app/online';
import { useSession } from '@/app/session';
import {
  AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

export function EmptyState({ title, description, action, className }: { title: string; description?: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <div data-slot="empty-state" className={cn('rounded-xl border border-beam-line bg-beam-paper px-6 py-10 text-center', className)}>
      <p className="font-medium">{title}</p>
      {description ? <p className="mx-auto mt-1 max-w-[60ch] text-beam-text-muted">{description}</p> : null}
      {action ? <div className="mt-4 flex justify-center">{action}</div> : null}
    </div>
  );
}

/**
 * The error panel's Retry stays in place while offline, disabled under the
 * "Offline" tooltip (docs/ux/shell.md "Required states"): a control that
 * vanishes reads as a different screen, one that is paused reads as waiting.
 */
export function ErrorPanel({ message, onRetry, offline = false, className }: { message: string; onRetry?: () => void; offline?: boolean; className?: string }) {
  const retry = onRetry ? <Button variant="outline" size="sm" disabled={offline} onClick={onRetry}>Retry</Button> : null;
  return (
    <div role="alert" data-slot="error-panel" className={cn('flex flex-col items-start gap-3 rounded-xl border border-beam-line border-l-[3px] border-l-beam-error bg-beam-paper px-5 py-4', className)}>
      <p className="font-medium">{message}</p>
      {retry && offline ? <Tooltip><TooltipTrigger asChild><span tabIndex={0} className="inline-flex">{retry}</span></TooltipTrigger><TooltipContent>Offline</TooltipContent></Tooltip> : retry}
    </div>
  );
}

export function OfflineBanner() {
  const online = useOnline();
  if (online) return null;
  return (
    <div role="alert" data-slot="offline-banner" className="flex min-h-8 items-center justify-center bg-beam-error-bg px-4 text-[13px] font-medium text-beam-error">
      You are offline. Actions are paused until the connection returns.
    </div>
  );
}

/**
 * Opens on the first 401 after a session existed. The page beneath is not
 * unmounted so unsaved text survives; the one action returns to this URL.
 */
export function SessionExpiredDialog() {
  const { status } = useSession();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    if (status !== 'authenticated') return;
    const onExpired = () => setOpen(true);
    window.addEventListener(UNAUTHENTICATED_EVENT, onExpired);
    return () => window.removeEventListener(UNAUTHENTICATED_EVENT, onExpired);
  }, [status]);
  const next = encodeURIComponent(location.pathname + location.search);
  return (
    <AlertDialog open={open}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Your session has ended.</AlertDialogTitle>
          <AlertDialogDescription>Sign in again to continue. You will return to this page.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={() => window.location.assign(`/auth/login?next=${next}`)}>Sign in again</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function RouteAnnouncer() {
  const text = useAnnouncement();
  return <div aria-live="polite" aria-atomic="true" className="sr-only">{text}</div>;
}

export function SkipLink() {
  return (
    <a href="#content" className="sr-only z-50 rounded-md bg-beam-paper px-3 py-2 text-sm font-medium text-foreground focus:not-sr-only focus:absolute focus:top-2 focus:left-2">
      Skip to content
    </a>
  );
}
